import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Info, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { InstallationDeleteConfirmDialog } from '@/features/installations/components/installation-delete-confirm-dialog'
import {
  useInstallationScrapCategoriesQuery,
  useInstallationScrapCategoryMutations
} from '@/features/installations/hooks/useInstallationConfiguration'
import { useInstallationDeleteConfirm } from '@/features/installations/hooks/use-installation-delete-confirm'

export function InstallationScrapCategoriesPage() {
  const { t } = useTranslation('installations')
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('installation', 'update')

  const listQuery = useInstallationScrapCategoriesQuery()
  const mutations = useInstallationScrapCategoryMutations()
  const deleteConfirm = useInstallationDeleteConfirm()

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [scrapName, setScrapName] = useState('')
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  const rows = listQuery.data

  const resetCreateForm = () => {
    setScrapName('')
  }

  const resetEditForm = () => {
    setEditingItemId(null)
    setEditingName('')
  }

  const submitCreate = async () => {
    const trimmed = scrapName.trim()
    if (!trimmed) {
      return
    }
    await mutations.addScrapCategory.mutateAsync(trimmed)
    resetCreateForm()
    setCreateOpen(false)
  }

  const submitEdit = async () => {
    const trimmed = editingName.trim()
    if (!trimmed || !editingItemId) {
      return
    }
    await mutations.patchScrapCategory.mutateAsync({
      itemId: editingItemId,
      name: trimmed
    })
    resetEditForm()
    setEditOpen(false)
  }

  const openEdit = (itemId: string, name: string) => {
    setEditingItemId(itemId)
    setEditingName(name)
    setEditOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.pendingDelete) {
      return
    }
    await mutations.removeScrapCategory.mutateAsync(deleteConfirm.pendingDelete.id)
    deleteConfirm.clearPendingDelete()
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (rows === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">{t('configuration.loadError')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('configuration.scrap.title')}
          </h1>
          <p className="text-muted-foreground">{t('configuration.scrap.description')}</p>
        </div>
        {canEdit && rows.length > 0 && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('common:actions.create')}
          </Button>
        )}
      </div>

      <Alert>
        <Info className="size-4 shrink-0" aria-hidden />
        <AlertDescription>{t('configuration.scrap.scopeNotice')}</AlertDescription>
      </Alert>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16">
          <Inbox className="size-10 text-muted-foreground/50" />
          <div className="space-y-1 text-center">
            <h3 className="text-lg font-semibold">{t('configuration.scrap.emptyTitle')}</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('configuration.scrap.emptyDescription')}
            </p>
          </div>
          {canEdit && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('common:actions.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="group/row">
                <TableHead
                  className={cn(
                    'bg-background group-hover/row:bg-muted',
                    'min-w-0 max-w-[75%] whitespace-normal'
                  )}
                >
                  {t('configuration.table.name')}
                </TableHead>
                {canEdit && (
                  <TableHead
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'w-[120px] min-w-0 max-w-[120px] text-end'
                    )}
                  >
                    {t('configuration.table.actions')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} className="group/row">
                  <TableCell
                    className={cn(
                      'bg-background group-hover/row:bg-muted',
                      'min-w-0 max-w-[75%] whitespace-normal'
                    )}
                  >
                    {row.name}
                  </TableCell>
                  {canEdit && (
                    <TableCell
                      className={cn(
                        'bg-background group-hover/row:bg-muted',
                        'w-[120px] min-w-0 max-w-[120px] text-end'
                      )}
                    >
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={t('configuration.a11y.editScrapCategory', {
                            name: row.name
                          })}
                          onClick={() => openEdit(row.id, row.name)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          aria-label={t('configuration.a11y.deleteScrapCategory', {
                            name: row.name
                          })}
                          onClick={() =>
                            deleteConfirm.requestDelete({
                              id: row.id,
                              label: row.name,
                            })
                          }
                          disabled={mutations.removeScrapCategory.isPending}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={createOpen}
        onOpenChange={open => {
          setCreateOpen(open)
          if (!open) {
            resetCreateForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('configuration.dialog.addScrapCategory')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              maxLength={200}
              placeholder={t('configuration.categoryPlaceholder')}
              value={scrapName}
              onChange={event => setScrapName(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateOpen(false)
                resetCreateForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!scrapName.trim() || mutations.addScrapCategory.isPending}
              onClick={submitCreate}
            >
              {t('common:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={open => {
          setEditOpen(open)
          if (!open) {
            resetEditForm()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('configuration.dialog.editScrapCategory')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Input
              maxLength={200}
              placeholder={t('configuration.categoryPlaceholder')}
              value={editingName}
              onChange={event => setEditingName(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditOpen(false)
                resetEditForm()
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!editingName.trim() || mutations.patchScrapCategory.isPending}
              onClick={submitEdit}
            >
              {t('configuration.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InstallationDeleteConfirmDialog
        pendingDelete={deleteConfirm.pendingDelete}
        open={deleteConfirm.isConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteConfirm.clearPendingDelete()
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.removeScrapCategory.isPending}
      />
    </div>
  )
}
